import { brandOf } from "./domains";

// Searches by job name, tag or keyword. An empty search matches everything.
export function filterJobs(jobs, search) {
  const q = search.trim().toLowerCase();
  return jobs.filter(j => {
    if (!q) return true;
    const nameMatch = (j.name || "").toLowerCase().includes(q);
    const tagMatch = (j.tags || []).some(t => t.toLowerCase().includes(q));
    const kwMatch = (j.keywords || []).some(k => k.toLowerCase().includes(q));
    return nameMatch || tagMatch || kwMatch;
  });
}

// The "Scan #" code shown in the report header and used by the MCP:
// "3f2a91c0-..." -> "3F2A91".
export function shortJobId(id) {
  return String(id || "").replace(/-/g, "").slice(0, 6).toUpperCase();
}

// Devices searched per keyword + location. Old jobs have none saved (desktop only).
function deviceCount(job) {
  return job.devices && job.devices.length > 0 ? job.devices.length : 1;
}

// Total searches in a job: rounds x keywords x locations x devices.
export function jobTotalUnits(job) {
  return job.rounds * job.keywords.length * job.locations.length * deviceCount(job);
}

// Minutes between samples: the same gaps the Worker plans them with (SPACING_MS).
const SPACING_MINUTES = { minute: 1, "15min": 15, hour: 60, day: 24 * 60 };

// The first and last planned sample times, worked out from the job alone (the
// Worker plans sample n at start + (n - 1) x spacing), so the Snapshots list
// needs no extra query. { first, last } as ISO strings, or nulls for an old
// job (no samples_per_run) or one with no start time.
export function plannedSampleSpan(job) {
  const minutes = SPACING_MINUTES[job.sample_spacing];
  const start = Date.parse(job.started_at);
  if (!job.samples_per_run || !minutes || Number.isNaN(start)) return { first: null, last: null };
  const last = start + (job.samples_per_run - 1) * minutes * 60000;
  return { first: new Date(start).toISOString(), last: new Date(last).toISOString() };
}

export const NO_NICHE = "No niche";

// The Snapshots list's niche groups: [{ key, name, jobs }]. Jobs keep their
// order (newest first), so groups are ordered by their newest snapshot.
// Jobs without a niche (older scans) go in "No niche", always last.
export function groupJobsByNiche(jobs) {
  const groups = new Map();
  for (const job of jobs) {
    const key = job.niche_id || "none";
    if (!groups.has(key)) groups.set(key, { key, name: job.niche_id ? job.niches?.name || "Unnamed niche" : NO_NICHE, jobs: [] });
    groups.get(key).jobs.push(job);
  }
  const list = [...groups.values()];
  return [...list.filter(g => g.key !== "none"), ...list.filter(g => g.key === "none")];
}

// The website in what was typed in the Audits search box, in the same form
// the report pins it ("https://www.acme.co.uk/contact" -> "acme.co.uk"), or
// null when it doesn't look like a website (no dot, or has spaces).
export function websiteInSearch(text) {
  const typed = String(text || "").trim();
  if (!typed.includes(".") || /\s/.test(typed)) return null;
  return brandOf(typed);
}

// Progress of a running job, for the scanning screen.
// Samples can overlap, so this is an estimate of the current round.
export function scanProgress(job) {
  const totalUnits = job ? jobTotalUnits(job) : 0;
  const currentRound = job && totalUnits > 0
    ? Math.min(Math.floor(job.completed_units / (job.keywords.length * job.locations.length * deviceCount(job))) + 1, job.rounds)
    : 1;
  return { totalUnits, currentRound };
}